import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Customer, customersService } from '../../services/clientsService';
import { Search, User, Building2, Mail, FileText, Loader2, X } from 'lucide-react';
import { ClientStatusBadge } from './ClientStatusBadge';
import { ClientTypeBadge } from './ClientTypeBadge';
import { formatCuit } from '../../utils/format';

interface ClientSelectorModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSelectClient: (client: Customer) => void;
}

const ClientSelectorModal: React.FC<ClientSelectorModalProps> = ({
    open,
    onOpenChange,
    onSelectClient
}) => {
    const [clients, setClients] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [showInactive, setShowInactive] = useState(false);

    useEffect(() => {
        if (open) {
            loadClients();
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [open]);

    const loadClients = async () => {
        try {
            setLoading(true);
            const data = await customersService.getAllCustomers();
            setClients(data);
        } catch (error) {
            console.error('Error loading clients:', error);
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    const getSearchableText = (client: Customer): string => {
        const baseText = `${client.cuit} ${client.email || ''}`;
        if (client.type === 'PERSON') {
            return `${baseText} ${client.firstName} ${client.lastName}`.toLowerCase();
        } else {
            return `${baseText} ${client.businessName}`.toLowerCase();
        }
    };

    const filteredClients = clients.filter(client => {
        const matchesSearch = getSearchableText(client).includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' ||
            (typeFilter === 'PERSON' && client.type === 'PERSON') ||
            (typeFilter === 'COMPANY' && client.type === 'COMPANY');
        const matchesStatus = showInactive || client.isActive;
        return matchesSearch && matchesType && matchesStatus;
    });

    const handleClientClick = (client: Customer) => {
        if (!client.isActive) return;
        onSelectClient(client);
        onOpenChange(false);
        setSearchTerm('');
        setTypeFilter('all');
        setShowInactive(false);
    };

    const handleClose = () => {
        onOpenChange(false);
        setSearchTerm('');
        setTypeFilter('all');
        setShowInactive(false);
    };

    const renderClientCard = (client: Customer) => {
        const isPerson = client.type === 'PERSON';
        const name = isPerson
            ? `${client.firstName} ${client.lastName}`
            : client.businessName;
        const email = isPerson ? client.email : client.contactEmail || client.email;
        const isInactive = !client.isActive;
        return (
            <div
                key={client.id}
                onClick={() => handleClientClick(client)}
                className={`
                    relative flex flex-col p-4 rounded-xl border transition-all duration-200
                    ${isInactive
                        ? 'cursor-not-allowed opacity-50 bg-muted/30 border-border'
                        : 'cursor-pointer bg-card border-border hover:border-primary/50 hover:shadow-md hover:shadow-primary/5 hover:-translate-y-0.5'
                    }
                `}
            >
                {/* Top: Icon + Name + Badges */}
                <div className="flex items-start gap-3 mb-3">
                    {/* Icon */}
                    <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                        {isPerson
                            ? <User className="w-4 h-4 text-muted-foreground" />
                            : <Building2 className="w-4 h-4 text-muted-foreground" />
                        }
                    </div>

                    {/* Name + Badges */}
                    <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate transition-colors ${isInactive ? 'text-muted-foreground' : 'text-foreground'}`}>
                            {name}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <ClientStatusBadge isActive={client.isActive} />
                            <ClientTypeBadge type={client.type} />
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-border/60 mb-3" />

                {/* Info rows */}
                <div className="space-y-2">
                    {email && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="truncate">{email}</span>
                        </div>
                    )}
                    {client.cuit && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{formatCuit(client.cuit)}</span>
                        </div>
                    )}
                </div>

                {/* Hover indicator (active only) */}
                {!isInactive && (
                    <div className="absolute inset-x-0 bottom-0 h-0.5 bg-primary rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
            </div>
        );
    };

    const renderLoadingSkeleton = () => {
        return Array.from({ length: 9 }).map((_, index) => (
            <div key={index} role="status" className="flex flex-col p-4 border rounded-xl animate-pulse bg-card border-border">
                <div className="flex items-start gap-3 mb-3">
                    <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
                    <div className="flex-1">
                        <div className="h-3.5 rounded-full bg-muted w-32 mb-2" />
                        <div className="h-2.5 rounded-full bg-muted w-20" />
                    </div>
                </div>
                <div className="border-t border-border/60 mb-3" />
                <div className="space-y-2">
                    <div className="h-2.5 rounded-full bg-muted w-full" />
                    <div className="h-2.5 rounded-full bg-muted w-24" />
                </div>
            </div>
        ));
    };

    if (!open) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative w-full h-full max-w-7xl mx-4 my-4 md:my-8 md:h-[90vh] flex flex-col bg-card rounded-2xl shadow-2xl border border-border overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-5 border-b border-border bg-muted/20">
                    <div className="flex-1">
                        <h2 className="text-xl font-bold text-foreground">Seleccionar Cliente</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            Busque y seleccione un cliente para continuar con la venta
                        </p>
                    </div>

                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg transition-colors hover:bg-muted text-muted-foreground hover:text-foreground"
                        aria-label="Cerrar"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Filters */}
                <div className="flex flex-col gap-3 px-6 py-4 border-b sm:flex-row border-border bg-background/50">
                    {/* Search */}
                    <div className="relative flex-1">
                        <Search className="absolute w-4 h-4 transform -translate-y-1/2 left-3 top-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Buscar por nombre, email o CUIT..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* Type Filter */}
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-full sm:w-[190px]">
                            <SelectValue placeholder="Tipo de cliente" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todos los tipos</SelectItem>
                            <SelectItem value="PERSON">
                                <div className="flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    <span>Persona</span>
                                </div>
                            </SelectItem>
                            <SelectItem value="COMPANY">
                                <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4" />
                                    <span>Empresa</span>
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Show Inactive Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) => setShowInactive(e.target.checked)}
                            className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-0 cursor-pointer"
                        />
                        <span className="text-sm text-foreground">Mostrar inactivos</span>
                    </label>
                </div>

                {/* Client Grid */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {loading ? (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {renderLoadingSkeleton()}
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className="flex items-center justify-center w-16 h-16 mb-5 rounded-2xl bg-muted">
                                <User className="w-8 h-8 text-muted-foreground" />
                            </div>
                            <h3 className="mb-1.5 text-lg font-semibold text-foreground">
                                No se encontraron clientes
                            </h3>
                            <p className="text-sm text-muted-foreground max-w-xs">
                                {searchTerm || typeFilter !== 'all'
                                    ? 'Intente ajustar sus filtros de búsqueda'
                                    : 'No hay clientes activos disponibles'}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {filteredClients.map(client => renderClientCard(client))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/20">
                    <p className="text-sm text-muted-foreground">
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Cargando clientes...
                            </span>
                        ) : (
                            `${filteredClients.length} cliente${filteredClients.length !== 1 ? 's' : ''} disponible${filteredClients.length !== 1 ? 's' : ''}`
                        )}
                    </p>
                    <Button variant="outline" onClick={handleClose}>
                        Cancelar
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ClientSelectorModal;
